import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Spinner, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiPause, FiPlay, FiXCircle, FiRotateCcw } from 'react-icons/fi';
import { emailBroadcastService } from '@/_services/api/emailIntegration/emailBroadcast';
import { getTeamIdFromURL } from '@/_helpers';
import EmailBroadcastStatusBadge from './EmailBroadcastStatusBadge';
import EmailBroadcastDetailModal from './EmailBroadcastDetailModal';
import Pagination from '@/components/Admin/Pagination';
import { BsEye, BsGraphUp } from 'react-icons/bs';
import { ActionStyles } from '@/components/Admin/reusableStyles';
import EmailBroadcastComposer from '@/components/Features/EmailIntegration/EmailBroadcast/EmailBroadcastComposer';
import Swal from 'sweetalert2';

const PAGE_LIMIT = 10;
const AUTO_REFRESH_INTERVAL = 10000;

// Action configuration to reduce repetition
const BROADCAST_ACTIONS = {
    pending: [
        { action: 'start', icon: FiPlay, label: 'Start', color: 'lightgreen' },
        { action: 'cancel', icon: FiXCircle, label: 'Cancel', color: 'delete' },
    ],
    sending: [
        { action: 'pause', icon: FiPause, label: 'Pause', color: 'edit' },
        { action: 'resume', icon: FiRotateCcw, label: 'Force Retry', color: 'edit' },
        { action: 'cancel', icon: FiXCircle, label: 'Cancel', color: 'delete' },
    ],
    paused: [
        { action: 'resume', icon: FiPlay, label: 'Resume', color: 'edit' },
        { action: 'cancel', icon: FiXCircle, label: 'Cancel', color: 'delete' },
    ],
    completed: [],
    failed: [],
    cancelled: [],
};

const EmailBroadcastDashboard = () => {
    const [broadcasts, setBroadcasts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedBroadcastId, setSelectedBroadcastId] = useState(null);
    const [selectedBroadcast, setSelectedBroadcast] = useState(null);
    const [showDetail, setShowDetail] = useState(false);
    const [showComposer, setShowComposer] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [actionLoading, setActionLoading] = useState({}); // Track loading per broadcast

    const teamId = getTeamIdFromURL();

    const fetchBroadcasts = useCallback(
        async (page = 1, status = statusFilter, skipLoading = false) => {
            if (!teamId) return;
            if (loading) return;

            let pageNum = Number(page);
            if (isNaN(pageNum) || pageNum < 1) {
                pageNum = 1;
            }
            if (!skipLoading) {
                setLoading(true);
            }
            try {
                const params = {
                    team: teamId,
                    page: pageNum,
                    limit: PAGE_LIMIT,
                };
                if (status !== 'all') params.status = status;

                const res = await emailBroadcastService.getBroadcasts(params);
                setBroadcasts(res?.results || []);
                setTotalRecords(res?.totalResults || 0);
                setCurrentPage(pageNum);
            } catch (err) {
                if (err?.name === 'AbortError') {
                    return;
                }
                toast.error(err?.message || 'Failed to load broadcasts');
                console.error('Fetch error:', err);
            } finally {
                setLoading(false);
            }
        },
        [teamId]
    );

    useEffect(() => {
        fetchBroadcasts(1, statusFilter);
    }, [teamId]);

    // Auto-refresh for sending broadcasts
    useEffect(() => {
        const hasSending = broadcasts.some((b) => b.status === 'sending');
        if (!hasSending) return;
        const id = setInterval(() => fetchBroadcasts(currentPage, statusFilter, true), AUTO_REFRESH_INTERVAL);
        return () => clearInterval(id);
    }, [broadcasts, fetchBroadcasts, currentPage, statusFilter]);

    // Optimistic update function
    const updateBroadcastStatus = (broadcastId, newStatus) => {
        setBroadcasts((prevBroadcasts) =>
            prevBroadcasts.map((b) => (b._id === broadcastId ? { ...b, status: newStatus } : b))
        );
    };

    const handleAction = async (action, broadcastId, currentStatus) => {
        // Set loading state for this specific broadcast
        setActionLoading((prev) => ({ ...prev, [broadcastId]: true }));

        try {
            // Optimistic update for status
            let newStatus = currentStatus;
            switch (action) {
                case 'start':
                    await emailBroadcastService.startBroadcast(broadcastId);
                    newStatus = 'sending';
                    break;
                case 'resume':
                    await emailBroadcastService.resumeBroadcast(broadcastId);
                    newStatus = 'sending';
                    break;
                case 'pause':
                    await emailBroadcastService.pauseBroadcast(broadcastId);
                    newStatus = 'paused';
                    break;
                case 'cancel':
                    const response = await Swal.fire({
                        title: `Are you sure to cancel ?`,
                        icon: 'danger',
                        showCancelButton: true,
                        confirmButtonColor: '#3085d6',
                        cancelButtonColor: '#d33',
                        confirmButtonText: 'Confirm',
                    });
                    if (!response.isConfirmed) {
                        return;
                    }
                    await emailBroadcastService.cancelBroadcast(broadcastId);
                    newStatus = 'cancelled';
                    break;
                default:
                    throw new Error(`Unknown action: ${action}`);
            }

            // Optimistically update the UI
            updateBroadcastStatus(broadcastId, newStatus);

            // Show success message with action label
            const actionLabels = {
                start: 'started',
                resume: 'resumed',
                pause: 'paused',
                cancel: 'cancelled',
            };
            toast.success(`Broadcast ${actionLabels[action] || action} successfully`);

            // Refresh data in background to ensure consistency
            fetchBroadcasts(currentPage, statusFilter, true);
        } catch (err) {
            toast.error(`Failed to ${action}: ${err.message}`);
            // If error, refresh to get actual status
            fetchBroadcasts(currentPage, statusFilter);
        } finally {
            setActionLoading((prev) => ({ ...prev, [broadcastId]: false }));
        }
    };

    const handlePageChange = (paginationData) => {
        const page = Number(paginationData?.currentPage || 1);
        if (page !== currentPage && page > 0) {
            fetchBroadcasts(page, statusFilter);
        }
    };

    const handleStatusFilterChange = (e) => {
        const newStatus = e.target.value;
        setStatusFilter(newStatus);
        setCurrentPage(1);
        fetchBroadcasts(1, newStatus);
    };

    const progressPct = (sent, total) => (total > 0 ? Math.round((sent / total) * 100) : 0);

    const renderActionButtons = (broadcast) => {
        const actions = BROADCAST_ACTIONS[broadcast.status] || [];
        const isLoading = actionLoading[broadcast._id];

        return (
            <ActionStyles>
                {/* View Button */}
                <BsEye
                    className="icon view"
                    onClick={() => {
                        setShowDetail(false);
                        setSelectedBroadcast(broadcast);
                        setShowComposer(true);
                    }}
                />

                {/* Stats Button */}
                <BsGraphUp
                    className="icon light-green"
                    onClick={() => {
                        setShowComposer(false);
                        setSelectedBroadcastId(broadcast._id);
                        setShowDetail(true);
                    }}
                />

                {/* Action Buttons */}
                {actions.map(({ action, icon: Icon, label, color }) => (
                    <Icon
                        key={action}
                        className={`icon ${color}`}
                        title={label}
                        onClick={() => !isLoading && handleAction(action, broadcast._id, broadcast.status)}
                        style={{
                            opacity: isLoading ? 0.5 : 1,
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                        }}
                    />
                ))}
            </ActionStyles>
        );
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Email Broadcasts</h2>
                <div className="d-flex gap-2">
                    <Form.Control
                        as="select"
                        size="sm"
                        style={{ width: 160 }}
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                    >
                        <option value="all">All statuses</option>
                        <option value="pending">Pending</option>
                        <option value="sending">Sending</option>
                        <option value="paused">Paused</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                    </Form.Control>
                    <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => fetchBroadcasts(currentPage, statusFilter)}
                        disabled={loading}
                    >
                        <FiRefreshCw className={loading ? 'spin' : ''} />
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                    <div className="mt-2 text-muted">Loading broadcasts...</div>
                </div>
            ) : (
                <>
                    <Table striped bordered hover responsive size="sm">
                        <thead>
                            <tr>
                                <th>Subject</th>
                                <th>Status</th>
                                <th>Progress</th>
                                <th>From</th>
                                <th>Created</th>
                                <th style={{ width: 200 }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {broadcasts.map((b) => {
                                const content = b?.emailBroadcastDraftSnapshot || b || {};
                                const pct = progressPct(b.sentCount || 0, b.totalRecipients || 0);

                                return (
                                    <tr key={b._id}>
                                        <td className="align-middle">
                                            {content.subject || '-'}
                                            {b?.abortReason && (
                                                <span
                                                    className="text-danger"
                                                    style={{
                                                        fontSize: '1.1rem',
                                                        display: 'block',
                                                        maxWidth: '300px',
                                                        wordWrap: 'break-word',
                                                    }}
                                                    title={b.abortReason}
                                                >
                                                    ({b.abortReason})
                                                </span>
                                            )}
                                        </td>
                                        <td className="align-middle">
                                            <EmailBroadcastStatusBadge status={b.status} />
                                        </td>
                                        <td className="align-middle">
                                            <div className="d-flex align-items-center gap-2">
                                                <div style={{ flex: 1, minWidth: 60 }}>
                                                    <div
                                                        style={{
                                                            height: 6,
                                                            width: '100%',
                                                            background: '#e5e7eb',
                                                            borderRadius: 3,
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                height: '100%',
                                                                width: `${pct}%`,
                                                                background:
                                                                    b.status === 'failed' ? '#ef4444' : '#10b981',
                                                                borderRadius: 3,
                                                                transition: 'width 0.3s',
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <small className="text-muted">
                                                    {b.sentCount || 0}/{b.totalRecipients || 0}
                                                </small>
                                            </div>
                                        </td>
                                        <td className="align-middle">{b.fromEmail}</td>
                                        <td className="align-middle">
                                            {b.createdAt ? new Date(b.createdAt).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="align-middle">{renderActionButtons(b)}</td>
                                    </tr>
                                );
                            })}
                            {broadcasts.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="text-center text-muted py-4">
                                        No broadcasts found.
                                        <br />
                                        <small>
                                            Use <code>/email</code> in a channel to create one.
                                        </small>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    {totalRecords > PAGE_LIMIT && (
                        <div className="mt-3 d-flex justify-content-center">
                            <Pagination
                                key={statusFilter}
                                totalRecords={totalRecords}
                                pageLimit={PAGE_LIMIT}
                                pageNeighbours={1}
                                pageNo={currentPage}
                                onPageChanged={handlePageChange}
                            />
                        </div>
                    )}
                </>
            )}

            <EmailBroadcastDetailModal
                show={showDetail}
                onHide={() => setShowDetail(false)}
                broadcastId={selectedBroadcastId}
            />
            <EmailBroadcastComposer
                show={showComposer}
                readOnly
                disabled
                existingDraft={selectedBroadcast}
                onClose={() => {
                    setShowComposer(false);
                    setSelectedBroadcast(null);
                }}
                teamId={teamId}
            />
        </div>
    );
};

export default EmailBroadcastDashboard;
